import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined'
import CallOutlinedIcon from '@mui/icons-material/CallOutlined'
import DirectionsWalkOutlinedIcon from '@mui/icons-material/DirectionsWalkOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined'
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined'
import { useEffect, useState } from 'react'
import type { EnumValue, KeyValue, PagaebleResult, SearchTareaDTO, SearchVendedorDTO, TareaPrioridad, TareaStatus, TareaTipo, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { daysFromToday, formatSpokenDateTime } from '../../../utils/dates'
import { personName } from '../../customers/common/customer-common'

export { AVATAR_TONES, errorMessage, formatCount, initials, labelize, pageWindow, personName } from '../../customers/common/customer-common'
export { formatSpokenDateTime } from '../../../utils/dates'

export const NOMBRE_MAX = 100
export const DESCRIPCION_MAX = 1000

export const TASKS_PATH = '/tareas'
export const NEW_TASK_PATH = `${TASKS_PATH}/nueva`

export const editTaskPath = (id: number) => `${TASKS_PATH}/${id}/edit`

export const PRIORITY_STYLES: Record<TareaPrioridad, string> = {
    baja: 'bg-tone-teal text-tone-teal-ink',
    media: 'bg-tone-orange text-tone-orange-ink',
    alta: 'bg-k-danger-soft text-k-danger-ink',
}

export const PRIORITY_DOTS: Record<TareaPrioridad, string> = {
    alta: 'bg-brand-pink',
    media: 'bg-brand-orange',
    baja: 'bg-k-ok',
}

export const PRIORITY_OPTIONS: Array<{ key: TareaPrioridad; label: string }> = [
    { key: 'alta', label: 'Alta' },
    { key: 'media', label: 'Media' },
    { key: 'baja', label: 'Baja' },
]

export const TYPE_ICONS: Record<TareaTipo, typeof TaskAltOutlinedIcon> = {
    tarea: TaskAltOutlinedIcon,
    cobranza: PaymentsOutlinedIcon,
    renovacion: AutorenewOutlinedIcon,
    cotizacion: RequestQuoteOutlinedIcon,
    siniestro: ReportProblemOutlinedIcon,
    llamada: CallOutlinedIcon,
    visita: DirectionsWalkOutlinedIcon,
    reunion: GroupsOutlinedIcon,
}

export const enumLabel = (value?: EnumValue | null) => value?.displayName || value?.name || '—'

export const isClosed = (status?: EnumValue<TareaStatus> | null) => status?.name === 'cerrada'

export interface DueView {
    label: string
    note: string
    noteClass: string
}

const daysText = (days: number) => `${days} ${days === 1 ? 'día' : 'días'}`

/** Fecha de vencimiento con aviso relativo; las tareas cerradas no muestran urgencia. */
export const dueView = (vencimiento?: string | null, status?: EnumValue<TareaStatus> | null): DueView => {
    const label = formatSpokenDateTime(vencimiento) || 'Sin definir'
    if (!vencimiento || isClosed(status)) return { label, note: '', noteClass: 'text-k-muted' }
    const days = daysFromToday(vencimiento)
    if (days == null) return { label, note: '', noteClass: 'text-k-muted' }
    if (days < 0) return { label, note: `Venció hace ${daysText(Math.abs(days))}`, noteClass: 'text-k-danger-ink' }
    if (days === 0) return { label, note: 'Vence hoy', noteClass: 'text-tone-orange-ink' }
    return { label, note: `Vence en ${daysText(days)}`, noteClass: days <= 3 ? 'text-tone-orange-ink' : 'text-k-muted' }
}

export interface SellerOption {
    id: number
    label: string
}

const CATALOG_ERROR = 'Error al cargar los catálogos de tareas'

export const fetchTaskCatalog = async (url: string): Promise<KeyValue[]> => {
    const payload: WrapperResponse<KeyValue[]> | null = await (await APIInvoke.invokeGET(url)).json()
    if (!payload?.ok) throw new Error(payload?.message || CATALOG_ERROR)
    return payload.body || []
}

export const fetchSellerOptions = async (): Promise<SellerOption[]> => {
    const payload: WrapperResponse<PagaebleResult<SearchVendedorDTO>> | null = await (await APIInvoke.invokeGET('/vendedores?size=99999&order=nombres asc')).json()
    return (payload?.ok ? payload.body?.items || [] : []).map((item) => ({ id: item.id, label: personName(item) }))
}

/** Total de tareas abiertas; `setCount` permite sincronizarlo cuando otra vista ya conoce el valor. */
export const useOpenTaskCount = () => {
    const [count, setCount] = useState<number | null>(null)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                const payload: WrapperResponse<PagaebleResult<SearchTareaDTO>> | null = await (await APIInvoke.invokeGET('/tareas?status=abierta&size=1')).json()
                if (active && payload?.ok) setCount(payload.body?.totalItems ?? 0)
            } catch (error) {
                console.error('Error al contar las tareas abiertas', error)
            }
        }
        load()
        return () => { active = false }
    }, [])

    return { count, setCount }
}

export interface FormMessage {
    type: 'error' | 'success'
    message: string
}

/** `state` de navegación para mostrar un aviso al volver a la lista. */
export interface TasksLocationState {
    backUrl?: string
    flash?: string
}
